import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type Appointment, type ExecutionStatus } from '../api';
import { useMe } from '../auth';
import { ErrorAlert, Loading, Pending } from '../components/ui';
import { isUnsynced, useLocalDrafts } from '../offline';
import { date, hm, localParts, longDate, timeOf } from '../format';
import { AppointmentItem } from './Agenda';
import { ExecStatus } from './Execucoes';
import { MessageStatusBadge, PURPOSE_LABEL } from './Mensagens';
import { novidadesPendentes } from './Novidades';
import { ReminderQueue } from './Alertas';

interface MyOverview {
  consultant: { id: string; name: string };
  today: string;
  projects: { id: string; code: string; name: string; status: string; clientName: string; isOwner: boolean }[];
  agendaToday: Appointment[];
  upcoming: Appointment[];
  pendingOutcome: Appointment[];
  drafts: { id: string; status: ExecutionStatus; serviceDate: string | null; quickNote: string | null; projectName: string | null; clientName: string | null }[];
  pending: Record<string, string>;
  messagesToReview: { id: string; purpose: import('../api').MessagePurpose; subject: string; status: import('../api').MessageStatus; statusReason: string | null }[];
  balanceWarnings: { id: string; code: string; projectName: string; clientName: string; balance: number; pendingOverageMinutes: number; low: boolean; noCredit: boolean }[];
}

// Meu ambiente (seção 3.2).
export function MeuAmbientePage() {
  const me = useMe();
  const localDrafts = useLocalDrafts(me.id).filter(isUnsynced);
  const q = useQuery({ queryKey: ['me-overview'], queryFn: () => api.get<MyOverview>('/me/overview'), refetchInterval: 120_000 });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorAlert error={q.error} />;
  const data = q.data!;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Olá, {data.consultant.name}</h1>
          <p>{longDate(data.today)}</p>
        </div>
        <div className="actions">
          <Link className="btn btn-primary" to="/execucoes/nova">
            Nova execução
          </Link>
          <Link className="btn" to="/agenda/novo">
            Novo compromisso
          </Link>
        </div>
      </div>
      {novidadesPendentes() && (
        <div className="alert alert-info">
          <strong>Novidades no aplicativo</strong>
          Registrar atendimento ficou mais simples (um clique na agenda, botão “Salvar e confirmar”, quadro do que falta).{' '}
          <Link to="/novidades">Veja o que mudou e como fazer</Link>
        </div>
      )}

      <ReminderQueue compact />

      {(data.pendingOutcome.length > 0 || localDrafts.length > 0) && (
        <section className="card" style={{ borderColor: '#f3d48a' }}>
          <h2>Precisa de atenção</h2>
          <div className="list">
            {localDrafts.length > 0 && (
              <Link className="list-item" to="/execucoes">
                <div className="title">
                  {localDrafts.length} rascunho(s) só neste aparelho <span className="badge badge-demo">não sincronizado</span>
                </div>
                <div className="meta">Serão enviados quando houver conexão. Limpar os dados do navegador os apaga.</div>
              </Link>
            )}
            {data.pendingOutcome.map((a) => (
              <Link key={a.id} className="list-item" to={`/agenda/${a.id}`}>
                <div className="title">
                  Resultado pendente: {a.title} <span className="badge badge-demo">registrar resultado</span>
                </div>
                <div className="meta">
                  <span>
                    {date(localParts(a.startsAt).date)} {timeOf(a.startsAt)}–{timeOf(a.endsAt)}
                  </span>
                  {a.clientName && <span>{a.clientName}</span>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="grid grid-2">
        <section className="card">
          <div className="card-head">
            <h2>Agenda de hoje</h2>
            <Link to="/agenda?v=dia" className="small">
              Abrir agenda
            </Link>
          </div>
          {data.agendaToday.length === 0 ? (
            <p className="muted">Nenhum compromisso hoje.</p>
          ) : (
            <div className="list">
              {data.agendaToday.map((a) => (
                <AppointmentItem key={a.id} a={a} />
              ))}
            </div>
          )}
          <h3 style={{ marginTop: '1rem' }}>Próximos 7 dias</h3>
          {data.upcoming.length === 0 ? (
            <p className="muted small">Nada agendado.</p>
          ) : (
            <div className="list">
              {data.upcoming.map((a) => (
                <Link key={a.id} className="list-item" to={a.private ? '/agenda' : `/agenda/${a.id}`}>
                  <div className="title">
                    <span className="muted small">
                      {date(localParts(a.startsAt).date).slice(0, 5)} {timeOf(a.startsAt)}
                    </span>{' '}
                    {a.title}
                  </div>
                  {a.clientName && <div className="meta">{a.clientName}</div>}
                </Link>
              ))}
            </div>
          )}
        </section>
        <section className="card">
          <div className="card-head">
            <h2>Execuções a completar</h2>
            <Link to="/execucoes" className="small">
              Meus registros
            </Link>
          </div>
          {data.drafts.length === 0 ? (
            <p className="muted">Nenhum rascunho ou execução aguardando conferência.</p>
          ) : (
            <div className="list">
              {data.drafts.map((d) => (
                <Link key={d.id} className="list-item" to={`/execucoes/${d.id}`}>
                  <div className="title">
                    {d.projectName ?? 'Pendente de vínculo'} <ExecStatus status={d.status} />
                  </div>
                  <div className="meta">
                    {d.clientName && <span>{d.clientName}</span>}
                    <span>{date(d.serviceDate)}</span>
                    {d.quickNote && <span>{d.quickNote.slice(0, 60)}</span>}
                  </div>
                </Link>
              ))}
            </div>
          )}
          <div className="grid" style={{ gap: '0.5rem', marginTop: '1rem' }}>
            <div>
              <h3>Avisos de saldo</h3>
              {data.balanceWarnings.length === 0 ? (
                <p className="muted small">Nenhum banco de horas com saldo baixo ou excedente.</p>
              ) : (
                <div className="list">
                  {data.balanceWarnings.map((w) => (
                    <Link key={w.id} className="list-item" to={`/bancos-de-horas/${w.id}`}>
                      <div className="title">
                        {w.clientName} · {w.code}{' '}
                        {w.noCredit ? (
                          <span className="badge badge-demo">crédito inicial não registrado</span>
                        ) : (
                          <span className="badge badge-demo">saldo {hm(w.balance)}</span>
                        )}
                        {w.pendingOverageMinutes > 0 && <span className="badge badge-demo">excedente {hm(w.pendingOverageMinutes)}</span>}
                      </div>
                      <div className="meta">{w.projectName}</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h3>Mensagens pendentes de conferência</h3>
              {data.messagesToReview.length === 0 ? (
                <p className="muted small">Nenhuma mensagem preparada aguardando envio.</p>
              ) : (
                <div className="list">
                  {data.messagesToReview.map((m) => (
                    <Link key={m.id} className="list-item" to={`/mensagens/${m.id}`}>
                      <div className="title">
                        {PURPOSE_LABEL[m.purpose]} <MessageStatusBadge status={m.status} />
                      </div>
                      <div className="meta">{m.subject}</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      <section className="card">
        <div className="card-head">
          <h2>Meus projetos ativos</h2>
          <Link to="/projetos" className="small">
            Ver todos os projetos da TECHNOVE
          </Link>
        </div>
        {data.projects.length === 0 ? (
          <p className="muted">Nenhum projeto ativo vinculado a você. Você pode consultar e registrar em qualquer projeto pela lista completa.</p>
        ) : (
          <div className="list">
            {data.projects.map((p) => (
              <Link key={p.id} className="list-item" to={`/projetos/${p.id}`}>
                <div className="title">
                  {p.name} {p.isOwner ? <span className="badge badge-brand">Responsável</span> : <span className="badge">Participante</span>}
                </div>
                <div className="meta">
                  <span>{p.clientName}</span>
                  <span>{p.code}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
